import axios from 'axios';

import { ServerValidationError } from '@apple/utils/api/errors';
import type { Guid } from '@apple/utils/primitive';

import type { StoreId } from '@apple/features/store/models';

import {
	type StoreEventModel,
	type StoreEventUpdateModel,
	storeEventModelSchema,
	storeEventUpdateModelSchema,
	storesByEventStatusResponseSchema,
} from '../models/management';
import type { StoreEventStatus } from '../models/shared';

/** @see AppleRetail.Web.Features.StoreEvents.StoreEventManagementController.GetById */
export async function getStoreEvent(
	storeEventId: Guid,
	signal?: AbortSignal,
): Promise<StoreEventModel> {
	const response = await axios.get<unknown>(`/api/management/store-events/${storeEventId}`, {
		signal,
	});
	return storeEventModelSchema.parse(response.data);
}

/** @see AppleRetail.Web.Features.StoreEvents.StoreEventManagementController.Post */
export async function createStoreEvent(
	storeId: StoreId,
	details: StoreEventUpdateModel,
): Promise<StoreEventModel> {
	const parsedDetails = storeEventUpdateModelSchema.parse(details);
	const response = await axios.post<unknown>(
		`/api/management/store-events/v2/${storeId}`,
		parsedDetails,
		{
			validateStatus: status => status < 300 || status === 409,
		},
	);

	if (response.status === 409) {
		throw new ServerValidationError<StoreEventUpdateModel>(
			new Error('Duplicate Store Events'),
			{
				title: 'Duplicate store events',
				type: 'tag:apple.arvato.com:validation',
				detail: 'A store event already exists for the selected store',
				status: response.status,
				instance: response.config.url,
				formErrors: ['A store event already exists for the selected store'],
			},
		);
	}

	return storeEventModelSchema.parse(response.data);
}

/** @see AppleRetail.Web.Features.StoreEvents.StoreEventManagementController.Save */
export async function saveStoreEvent(storeEventId: Guid): Promise<void> {
	await axios.post(`/api/store-events/${storeEventId}/save`);
}

/** @see AppleRetail.Web.Features.StoreEvents.StoreEventManagementController.Put */
export async function updateStoreEvent(
	storeEventId: Guid,
	details: StoreEventUpdateModel,
): Promise<void> {
	const parsedDetails = storeEventUpdateModelSchema.parse(details);
	await axios.put(`/api/management/store-events/${storeEventId}`, parsedDetails);
}

/** @see AppleRetail.Web.Features.StoreEvents.StoreEventManagementController.Delete */
export async function cancelStoreEvent(storeEventId: Guid): Promise<void> {
	await axios.delete(`/api/management/store-events/${storeEventId}`);
}

/** @see AppleRetail.Web.Areas.StoreEvents.ApiControllers.StoreEventCompletionApiController.Complete */
export async function completeStoreEvent(storeEventId: Guid): Promise<void> {
	await axios.post(`/api/store-events/${storeEventId}/complete`);
}

/** @see AppleRetail.Web.Areas.StoreEvents.ApiControllers.StoreEventCompletionApiController.Submit */
export async function submitStoreEvent(storeEventId: Guid): Promise<void> {
	await axios.post<void>(`/api/store-events/${storeEventId}/submit`);
}

/** @see AppleRetail.Web.Areas.StoreEvents.ApiControllers.StoreEventCompletionApiController.Archive */
export async function archiveStoreEvent(storeEventId: Guid): Promise<void> {
	await axios.post(`/api/store-events/${storeEventId}/archive`);
}

/** @see AppleRetail.Web.Areas.StoreEvents.ApiControllers.StoreEventCompletionApiController.Archive */
export async function getStoreIdsForExistingEvents(signal?: AbortSignal): Promise<StoreId[]> {
	const { data } = await axios.get<unknown>('/api/store-events/stores/by-status', {
		params: {
			storeEventStates: [
				'NewEvent',
				'Saved',
				'ReadyForSubmission',
				'SubmissionPending',
				'Submitted',
				'Open',
				'Complete',
				'EarlyManagementKitPicked',
				'EarlyManagementKitShipped',
				'EarlyManagementKitDelivered',
				'FullKitPicked',
				'FullKitShipped',
			] satisfies StoreEventStatus[],
		},
		signal,
	});

	return storesByEventStatusResponseSchema.parse(data).storeIds;
}
